
import { Component, Vue, Prop } from "vue-property-decorator";
import { SignUpRequest, SignUpResponseError } from "@/models/SignUp";
import SignUpFormTextInput from "./SignUpFormTextInput.vue";
import SignUpFormAddressInputSuggest from "./SignUpFormAddressInputSuggest.vue";
import CustomerService from "@/services/CustomerService";
import CheckoutService from "@/services/CheckoutService";
import { CheckoutCountryModel } from "@/models/Checkout";
import { NewCustomerConfig } from "@/models/CbxVariables";
import { CbxVariablesModule } from "@/store/modules/CbxVariablesModule";

const service: CustomerService = new CustomerService();
const checkoutService: CheckoutService = new CheckoutService();

@Component({
    name: "SignUpForm",
    components: {
        SignUpFormTextInput,
        SignUpFormAddressInputSuggest,
    }
})

export default class SignUpForm extends Vue {
    @Prop({ type: String, required: false, default: "" }) title: string;
    @Prop({ type: String, required: false, default: "" }) description: string;
    @Prop({ type: Boolean, required: false, default: false }) isB2B: boolean;

    password: string = null
    repeatedPassword: string = null;
    birthdayDay: number = null;
    birthdayMonth: number = null;
    birthdayYear: number = null;
    consent: boolean = false;
    completed: boolean = false;
    error: SignUpResponseError = SignUpResponseError.None;

    countries: CheckoutCountryModel[] = [];

    model: SignUpRequest = null;
    sending: boolean = false;

    get translationsReady(): boolean {
        return this.$root.$data.translationsLoaded === true;
    }

    get config(): NewCustomerConfig {
        return CbxVariablesModule.VARIABLES.newCustomerConfig;
    }

    get showHeader(): boolean {
        return this.title.length > 0 || this.description.length > 0;
    }

    get suggestAddress(): boolean {
        if (!this.model.country)
            return false;

        return this.countries.find(c => c.code == this.model.country)?.service != null;
    }

    get isError() {
        return this.error !== SignUpResponseError.None;
    }

    get eanRule(): boolean {
        if (this.model.eanno != null && this.model.eanno != "") {
            return true;
        }
        else {
            return false;
        }
    }

    get errorMessage(): string {
        if (this.error === SignUpResponseError.MissingDetails)
            return String(this.$t("signup.error-missing-details", ["Please provide the nessesary details."]));
        else if (this.error === SignUpResponseError.CustomerExists)
            return String(this.$t("signup.error-customer-exists", ["A customer already exist with provided details."]));
        else if (this.error === SignUpResponseError.UserExists)
            return String(this.$t("signup.error-user-exists", ["A user already exist with provided email address."]));

        return String(this.$t("signup.error-message", ["The system encountered an error, please contact our support."]));
    }

    get passwordMismatch(): boolean {
        if (this.password && this.repeatedPassword && this.password != this.repeatedPassword)
            return true;

        return false;
    }

    get passwordMatchRules(): string {
        if (this.password && this.repeatedPassword)
            return "required";

        return "";
    }
    
    get birthdayRules(): string {
        if (this.birthdayDay || this.birthdayMonth || this.birthdayYear)
            return "required";

        return "";
    }

    get birthdayIsValid(): boolean {
        if (this.birthdayDay || this.birthdayMonth || this.birthdayYear)
            return !isNaN(Date.parse(this.model.birthday));

        return true;
    }

    get birthdayYears(): number[] {
        const today = new Date();
        return Array.from({ length: 100 }, (value, index) => index).map(n => today.getFullYear() - n);
    }

    updatePassword(): string {
        if (this.password && this.repeatedPassword && this.password == this.repeatedPassword)
            this.model.password = this.password;

        return "";
    }

    updateBirthday() {
        if (this.birthdayDay && this.birthdayMonth && this.birthdayYear)
            this.model.birthday = `${this.birthdayYear}-${this.birthdayMonth < 10 ? "0" : ""}${this.birthdayMonth}-${this.birthdayDay < 10 ? "0" : ""}${this.birthdayDay}`;
        else
            this.model.birthday = null;

        console.log(this.$refs);
        (this.$refs.birthday as HTMLElement).focus();
        (this.$refs.birthday as HTMLElement).blur();
    }

    reset() {
        this.completed = false;
        this.error = SignUpResponseError.None;
    }

    async send(validate: () => boolean) {
        const isValid = await validate();
        if (!isValid || this.sending) {
            return;
        }

        // sanitize phone
        this.model.phone = this.model.phone?.replaceAll(' ', '');

        this.sending = true;
        const res = await service.signUp(this.model);

        if (res.signupcompleted === true)
            this.completed = true;
        else
            this.error = res.error;

        this.sending = false;
    }

    handleSuggest(input: any) {
        if (input.suggestion) {
            this.model.address = input.suggestion;
        } else {
            this.model.address = input.address;
            this.model.zipcode = input.zipcode;
            this.model.city = input.city;
        }
    }

    private async created() {
        if (this.isB2B)
            this.countries = await checkoutService.getCountries();

        this.model = {
            name: null,
            email: null,
            country: this.countries?.[0]?.code,
        };
    }
}
